import React from 'react';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Search, FileCheck, Send, Banknote, ArrowRight, Shield, Clock, CheckCircle } from 'lucide-react';

const HowItWorksPage = () => {
  const steps = [
    {
      number: '01',
      icon: Search,
      title: 'Find Your Agreements',
      description: 'We locate all your finance agreements going back to 2007 using just your name, date of birth, and current address. No paperwork needed — even if you have moved address, changed your name, or have no documents whatsoever. The search takes just minutes and does not affect your credit score.',
      highlight: 'No paperwork required'
    },
    {
      number: '02',
      icon: FileCheck,
      title: 'We Assess Your Claim',
      description: 'Our expert legal team reviews each of your finance agreements to identify undisclosed commission arrangements. We analyse the terms, calculate potential compensation, and determine the strength of your claim against each lender.',
      highlight: 'Expert legal review'
    },
    {
      number: '03',
      icon: Send,
      title: 'We Submit Your Claim',
      description: 'BlueLion handles all communication with lenders on your behalf. We prepare and submit your formal complaint, manage all correspondence, and if necessary, escalate your case to the Financial Ombudsman Service. You do not need to contact anyone yourself.',
      highlight: 'We handle everything'
    },
    {
      number: '04',
      icon: Banknote,
      title: 'You Receive Compensation',
      description: 'We operate on a No Win, No Fee basis — we only get paid when you do. Our success fee is a maximum of 36% (inclusive of VAT), and you have a 14-day cooling-off period.',
      highlight: 'No win, no fee'
    }
  ];

  const benefits = [
    { icon: Shield, text: 'SRA Regulated Law Firm' },
    { icon: Clock, text: 'Claims back to 2007' },
    { icon: CheckCircle, text: 'No Win, No Fee' }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 pt-24">
        {/* Hero Section */}
        <section className="py-12 lg:py-16 bg-background">
          <div className="section-container">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground leading-tight mb-6">
                How It Works
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Claiming compensation for mis-sold car finance is straightforward with BlueLion. 
                We handle everything from start to finish — your only job is to start the search.
              </p>
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap justify-center gap-6 mb-16">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-center gap-2 px-4 py-2 rounded-full bg-muted border border-border">
                  <benefit.icon className="w-5 h-5 text-accent" />
                  <span className="text-sm font-medium text-foreground">{benefit.text}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Steps Section */}
        <section className="py-16 lg:py-24 bg-muted/30">
          <div className="section-container">
            <div className="space-y-8">
              {steps.map((step, index) => (
                <Card key={index} className="border-border overflow-hidden">
                  <CardContent className="p-0">
                    <div className="grid lg:grid-cols-12 gap-0">
                      {/* Step Number */}
                      <div className="lg:col-span-2 bg-primary p-8 flex items-center justify-center">
                        <span className="font-heading text-5xl lg:text-6xl font-bold text-gold">
                          {step.number}
                        </span>
                      </div>
                      
                      {/* Step Content */}
                      <div className="lg:col-span-10 p-8 lg:p-10">
                        <div className="flex items-start gap-4">
                          <div className="w-14 h-14 rounded-xl bg-accent/10 flex items-center justify-center flex-shrink-0">
                            <step.icon className="w-7 h-7 text-accent" />
                          </div>
                          <div className="flex-1">
                            <div className="flex flex-wrap items-center gap-3 mb-3">
                              <h2 className="font-heading text-xl sm:text-2xl font-bold text-foreground">
                                {step.title}
                              </h2>
                              <span className="px-3 py-1 text-xs font-semibold rounded-full bg-gold/10 text-gold">
                                {step.highlight}
                              </span>
                            </div>
                            <p className="text-muted-foreground leading-relaxed">
                              {step.description}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* What You Need Section */}
        <section className="py-16 lg:py-20 bg-background">
          <div className="section-container">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground mb-6">
                What You Need to Get Started
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                Just three pieces of information — that's all we need to find your agreements:
              </p>
              
              <div className="grid sm:grid-cols-3 gap-6 mb-12">
                <Card className="border-border">
                  <CardContent className="p-6 text-center">
                    <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-4">
                      <span className="text-2xl font-bold text-accent">1</span>
                    </div>
                    <h3 className="font-semibold text-foreground">Your Name</h3>
                  </CardContent>
                </Card>
                
                <Card className="border-border">
                  <CardContent className="p-6 text-center">
                    <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-4">
                      <span className="text-2xl font-bold text-accent">2</span>
                    </div>
                    <h3 className="font-semibold text-foreground">Date of Birth</h3>
                  </CardContent>
                </Card>
                
                <Card className="border-border">
                  <CardContent className="p-6 text-center">
                    <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-4">
                      <span className="text-2xl font-bold text-accent">3</span>
                    </div>
                    <h3 className="font-semibold text-foreground">Current Address</h3>
                  </CardContent>
                </Card>
              </div>
              
              <p className="text-muted-foreground">
                No Car Finance paperwork required. No Hassle. We do the rest.
              </p>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 lg:py-20 bg-primary">
          <div className="section-container">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-primary-foreground mb-6">
                Ready to Find Out If You're Owed Compensation?
              </h2>
              <p className="text-lg text-primary-foreground/80 mb-8">
                It takes a few minutes to start your claim. Click below to begin your eligibility check now.
              </p>
              <Button variant="gold" size="lg" asChild>
                <a href="https://signup.bluelionclaims.co.uk/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2">
                  Find Your Agreements
                  <ArrowRight className="w-5 h-5" />
                </a>
              </Button>
              <p className="text-sm text-primary-foreground/60 mt-4">
                No win, no fee. No paperwork required.
              </p>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default HowItWorksPage;
